import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as THREE from "three";
import RueCard from "./objects/RueCard";
import S20024Card from "./objects/S20024Card";
import CardCase from "./objects/CardCase";
import { CardState, CardProps } from "./utils";
import InfoButton from "../components/InfoButton";

const CameraInitialDistanceMobile = 25;
const CameraInitialDistanceDesktop = 15;
const HoverOffset = 0.2;
const AnimationSpeed = 5;
const CameraZoomedPosition = [0, 0, 8] as const;
const CameraZoomedPositionMobile = [0, 0, 12] as const;

const CardAnimationManager = ({ isMobile }: { isMobile: boolean }) => {
  const { camera } = useThree();
  const [cards, setCards] = useState<CardState[]>([
    {
      id: 's20024',
      originalPosition: [0, -6.2, 0.1],
      currentPosition: [0, -6.2, 0.1],
      targetPosition: [0, -6.2, 0.1],
      isHovered: false,
      isAtCenter: false,
    },
    {
      id: 'rue',
      originalPosition: [0, -6.7, 0.2],
      currentPosition: [0, -6.7, 0.2],
      targetPosition: [0, -6.7, 0.2],
      isHovered: false,
      isAtCenter: false,
    },
  ]);

  const initialCameraPosition = useRef(camera.position.clone());
  const targetCameraPosition = useRef(camera.position.clone());
  const currentCameraPosition = useRef(camera.position.clone());
  
  // タッチ操作用の状態管理
  const [touchState, setTouchState] = useState({
    startX: 0,
    isDragging: false,
    offsetX: 0,
  });
  
  // カメラの初期ターゲット位置を保存
  const initialCameraTarget = useRef(new THREE.Vector3(0, 0, 0));
  const targetCameraTarget = useRef(new THREE.Vector3(0, 0, 0));

  const handleCardHover = useCallback((cardId: string, isHovered: boolean) => {
    setCards(prevCards => 
      prevCards.map(card => {
        if (card.id === cardId && !card.isAtCenter) {
          const newCard = { ...card, isHovered };
          if (isHovered) {
            newCard.targetPosition = [
              card.originalPosition[0],
              card.originalPosition[1] + HoverOffset,
              card.originalPosition[2]
            ];
          } else {
            newCard.targetPosition = [...card.originalPosition];
          }
          return newCard;
        }
        return card;
      })
    );
  }, []);

  const handleCardClick = useCallback((cardId: string) => {
    setCards(prevCards => 
      prevCards.map(card => {
        const newCard = { ...card };
        
        if (card.id === cardId) {
          if (!card.isAtCenter) {
            newCard.targetPosition = [0, 0, card.originalPosition[2]];
            newCard.isAtCenter = true;
            newCard.isHovered = false;
            
            const zoomPosition = isMobile ? CameraZoomedPositionMobile : CameraZoomedPosition;
            targetCameraPosition.current = new THREE.Vector3(...zoomPosition);
            targetCameraTarget.current = new THREE.Vector3(0, 0, 0);
          }
        } else {
          if (card.isAtCenter) {
            newCard.targetPosition = [...card.originalPosition];
            newCard.isAtCenter = false;
            newCard.isHovered = false;
          }
        }
        
        return newCard;
      })
    );
  }, [isMobile]);

  // タッチ開始時の処理
  const handleTouchStart = useCallback((event: TouchEvent) => {
    if (!isMobile) return;
    
    const centerCard = cards.find(card => card.isAtCenter);
    if (!centerCard) return;

    const touch = event.touches[0];
    setTouchState({
      startX: touch.clientX,
      isDragging: true,
      offsetX: 0,
    });
  }, [isMobile, cards]);

  // タッチ移動時の処理
  const handleTouchMove = useCallback((event: TouchEvent) => {
    if (!isMobile || !touchState.isDragging) return;
    
    const centerCard = cards.find(card => card.isAtCenter);
    if (!centerCard) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - touchState.startX;
    const normalizedDelta = deltaX * 0.01; // スライド感度調整

    setTouchState(prev => ({
      ...prev,
      offsetX: normalizedDelta,
    }));

    // カードの位置とカメラを連動して移動
    setCards(prevCards =>
      prevCards.map(card => {
        if (card.isAtCenter) {
          return {
            ...card,
            targetPosition: [normalizedDelta, 0, card.originalPosition[2]],
          };
        }
        return card;
      })
    );

    // カメラのposition,targetも一緒に移動
    const zoomPosition = isMobile ? CameraZoomedPositionMobile : CameraZoomedPosition;
    targetCameraPosition.current = new THREE.Vector3(
      normalizedDelta + zoomPosition[0],
      zoomPosition[1],
      zoomPosition[2]
    );
    targetCameraTarget.current = new THREE.Vector3(normalizedDelta, 0, 0);
  }, [isMobile, touchState.isDragging, touchState.startX, cards]);

  // タッチ終了時の処理
  const handleTouchEnd = useCallback(() => {
    if (!isMobile) return;
    
    setTouchState({
      startX: 0,
      isDragging: false,
      offsetX: 0,
    });
  }, [isMobile]);

  // カード以外の場所をクリックしたときに元の位置に戻す処理
  const handleBackgroundClick = useCallback(() => {
    setCards(prevCards => 
      prevCards.map(card => {
        if (card.isAtCenter) {
          return {
            ...card,
            targetPosition: [...card.originalPosition],
            isAtCenter: false,
            isHovered: false,
          };
        }
        return card;
      })
    );
    // カメラのpositionとtargetも初期状態に戻す
    targetCameraPosition.current = initialCameraPosition.current.clone();
    targetCameraTarget.current = initialCameraTarget.current.clone();
    
    // タッチ状態もリセット
    setTouchState({
      startX: 0,
      isDragging: false,
      offsetX: 0,
    });
  }, []);

  // タッチイベントリスナーの設定
  useEffect(() => {
    if (!isMobile) return;

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, isMobile]);

  useFrame((state, delta) => {
    setCards(prevCards => 
      prevCards.map(card => {
        const newCard = { ...card };
        newCard.currentPosition = [
          card.currentPosition[0] + (card.targetPosition[0] - card.currentPosition[0]) * AnimationSpeed * delta,
          card.currentPosition[1] + (card.targetPosition[1] - card.currentPosition[1]) * AnimationSpeed * delta,
          card.currentPosition[2] + (card.targetPosition[2] - card.currentPosition[2]) * AnimationSpeed * delta,
        ];
        return newCard;
      })
    );

    // カメラのposition更新
    currentCameraPosition.current.lerp(targetCameraPosition.current, AnimationSpeed * delta);
    camera.position.copy(currentCameraPosition.current);
    
    // OrbitControlsのターゲットも更新
    const controls = state.controls as any; // OrbitControlsの型回避
    if (controls && controls.target) {
      controls.target.lerp(targetCameraTarget.current, AnimationSpeed * delta);
      controls.update();
    }
  });

  return (
    <>
      {/* 背景クリック用の透明なメッシュ */}
      <mesh
        position={[0, 0, -10]}
        onClick={handleBackgroundClick}
        visible={false}
      >
        <planeGeometry args={[100, 100]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      <CardCase position={[0, -7, 0]} rotation={[0, 0, 0]} />

      {cards.map(card => {
        const commonProps: CardProps = {
          id: card.id,
          position: card.currentPosition,
          rotation: [0, 0, 0],
          isHovered: card.isHovered,
          onMouseEnter: () => handleCardHover(card.id, true),
          onMouseLeave: () => handleCardHover(card.id, false),
          onClick: () => handleCardClick(card.id),
        };

        if (card.id === 's20024') {
          return <S20024Card key={card.id} {...commonProps} />;
        } else if (card.id === 'rue') {
          return <RueCard key={card.id} {...commonProps} />;
        }
        return null;
      })}
    </>
  );
};

export default () => {
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const orbitControlsRef = useRef<any>(null); // OrbitControlsの参照

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent.toLowerCase()) || window.innerWidth <= 768);
    };

    const handleResize = () => { checkMobile(); };
    checkMobile();

    window.addEventListener('resize', handleResize);
    return () => { window.removeEventListener('resize', handleResize); };
  }, []);

  const handleBackClick = () => {
    navigate('/');
  };

  return (
    <div style={{ position: 'relative', height: '100vh', width: '100vw' }}>
      <button
        onClick={handleBackClick}
        style={{
          position: 'absolute',
          top: isMobile ? '10px' : '20px',
          left: isMobile ? '10px' : '20px',
          zIndex: 1000,
          color: '#ffffff',
          padding: '10px 15px',
          fontSize: '20px',
          cursor: 'pointer',
          fontFamily: 'Arial, sans-serif',
          border: 'none',
          backgroundColor: 'rgba(0, 0, 0, 0)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#808080';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = '#ffffff';
        }}
      >
        &lt; Back
      </button>

      <Canvas
        camera={{ fov: 50, position: [0, 0, (isMobile ? CameraInitialDistanceMobile : CameraInitialDistanceDesktop)] }}
        style={{ height: '100vh', width: '100vw' }}
      >
        <color attach="background" args={['#1a1a1a']} />
        <OrbitControls 
          ref={orbitControlsRef}
          enablePan={false} 
          enableZoom={false} 
          enableRotate={false} 
          target={[0, 0, 0]} 
        />

        <CardAnimationManager isMobile={isMobile} />
      </Canvas>
      <InfoButton scene="CardCase" />
    </div>
  );
};
